import { api } from '@mbscan/client-api'
import { isSome } from '@mbscan/shared'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useRef, useState } from 'react'
import Webcam from 'react-webcam'

import { ScannerScreen } from '@/components/screens/scanner-screen'
import { cropCapturedImage } from '@/features/scan/image-crop'
import { useScanSession } from '@/features/scan/scan-session-context'
import type { AspectRatio } from '@/features/scan/types'
import { useActiveTenant } from '@/features/tenant/tenant-context'

function ScanPage() {
  const tenant = useActiveTenant()
  const session = useScanSession()
  const navigate = useNavigate()
  const webcamRef = useRef<Webcam>(null)
  const [cameraError, setCameraError] = useState(false)
  const today = dayjs().format('YYYY-MM-DD')
  const { data: todayResults } = api.useFetchMetalTagScanResultsQuery({
    variables: { date: today, filter: 'all', take: 200 },
    pollInterval: session.pendingScans.some(({ status }) => status === 'processing') ? 5_000 : 0,
  })
  const serverFailureCount =
    todayResults?.metalTagScanResults.items.filter(({ kind }) => kind === 'failure').length ?? 0
  const scanFailureCount = serverFailureCount + session.pendingScans.filter(({ status }) => status === 'failed').length
  // 履歴バッジは「当日のスキャン数」を表示する。撮影画面のマウント中に撮った枚数だと
  // 撮影 → 履歴 → 撮影 と往復するたびに 0 に戻り、その日の作業量が読み取れなくなるため。
  // サーバーに未反映のもの（アップロード中・解析待ち）は history 画面と同じ方法で足し込む。
  const serverClientRequestIds = new Set(
    todayResults?.metalTagScanResults.items.flatMap(
      (entry) => entry.scanLog?.clientRequestId ?? entry.failure?.clientRequestId ?? [],
    ) ?? [],
  )
  const unreflectedCount = session.pendingScans.filter(
    ({ capturedAt, clientRequestId }) =>
      dayjs(capturedAt).format('YYYY-MM-DD') === today && !serverClientRequestIds.has(clientRequestId),
  ).length
  const todayScanCount = (todayResults?.metalTagScanResults.totalCount ?? 0) + unreflectedCount
  const navigateHome = () => {
    void navigate({ to: '/t/$tenant_id', params: { tenant_id: tenant.id } })
  }
  const navigateHistory = () => {
    void navigate({ to: '/t/$tenant_id/millsheet/history', params: { tenant_id: tenant.id } })
  }
  const capture = async (aspectRatio: AspectRatio, buildingPart: string) => {
    const imageSource = webcamRef.current?.getScreenshot()
    if (!isSome(imageSource)) {
      setCameraError(true)
      return false
    }
    try {
      const imageBlob = await cropCapturedImage(imageSource, aspectRatio)
      session.enqueueCapture(imageBlob, buildingPart.length > 0 ? buildingPart : null)
      return true
    } catch {
      setCameraError(true)
      return false
    }
  }

  return (
    <>
      <ScannerScreen
        tenant={tenant}
        buildingPartOptions={tenant.buildingParts}
        cameraPreview={
          cameraError ? (
            <div className="camera-permission-error" role="alert">
              <strong>カメラを利用できません</strong>
              <span>ブラウザの設定からカメラを許可して、ページを再読み込みしてください。</span>
            </div>
          ) : (
            <Webcam
              ref={webcamRef}
              audio={false}
              className="camera-webcam"
              forceScreenshotSourceSize
              screenshotFormat="image/png"
              videoConstraints={{
                facingMode: { ideal: 'environment' },
                height: { ideal: 2160 },
                width: { ideal: 3840 },
              }}
              onUserMediaError={() => {
                setCameraError(true)
              }}
            />
          )
        }
        externalStatus={{
          capturedCount: todayScanCount,
          failedSaveCount: session.failedSaveCount,
          lastImageUrl: session.lastImageUrl,
          retrying: session.retrying,
          scanFailureCount,
          unsavedCount: session.unsavedCount,
        }}
        onCapture={capture}
        onRetrySaves={session.retryFailed}
        onBack={navigateHome}
        onResults={navigateHistory}
      />
    </>
  )
}

export const Route = createFileRoute('/t/$tenant_id/millsheet/scan')({ component: ScanPage })
